import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Stack,
  CircularProgress,
  alpha,
  Chip,
} from "@mui/material";
import dayjs from "dayjs";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import { useTheme } from "@mui/material/styles";

const RequirementHistoryDialog = ({
  open,
  onClose,
  sourceColumn,
  requirementName,
  history,
  loading,
  currentValue,
  currentStatus,
}) => {
  const theme = useTheme();

  const isAvailable = (value) => value === true;

  const getStatusLabel = (value) => {
    return isAvailable(value) ? "Available" : "None";
  };

  const getStatusColor = (value) => {
    return isAvailable(value) ? theme.palette.success.main : theme.palette.error.main;
  };

  const getActionColor = (action) => {
    switch (action) {
      case "Created":
        return theme.palette.success.main;
      case "Updated":
        return theme.palette.info.main;
      case "Deleted":
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const currentIsAvailable = isAvailable(currentValue);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "1.5rem",
          bgcolor: theme.palette.background.default,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <HistoryRoundedIcon sx={{ color: theme.palette.primary.main }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {requirementName || sourceColumn || "Requirement"} History
          </Typography>
          {sourceColumn && (
            <Typography variant="caption" color="text.secondary">
              {sourceColumn}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, minHeight: 120 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 4,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : !history || history.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No history available for this requirement yet.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Changes will appear here once the requirement is updated.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Current Status Summary */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Current Status
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {currentIsAvailable ? (
                  <CheckRoundedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.success.main }}
                  />
                ) : (
                  <ClearRoundedIcon
                    fontSize="small"
                    sx={{ color: theme.palette.error.main }}
                  />
                )}
                <Typography variant="body2" fontWeight={600}>
                  {getStatusLabel(currentValue)}
                </Typography>
                {currentStatus && (
                  <Chip
                    size="small"
                    label={currentStatus}
                    sx={{
                      bgcolor: getStatusColor(currentValue),
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Timeline */}
            <Stack spacing={0}>
              {history.map((entry, index) => {
                const isLast = index === history.length - 1;
                const actionColor = getActionColor(entry.action);
                const entryValue = entry.changes?.value ?? currentValue;
                const entryIsAvailable = isAvailable(entryValue);

                return (
                  <Box key={entry.id || index}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        py: 1.5,
                      }}
                    >
                      {/* Timeline dot + line */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          flexShrink: 0,
                          width: 20,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: actionColor,
                            flexShrink: 0,
                            mt: 0.5,
                          }}
                        />
                        {!isLast && (
                          <Box
                            sx={{
                              width: 2,
                              flex: 1,
                              bgcolor: alpha(theme.palette.divider, 0.5),
                              minHeight: 20,
                            }}
                          />
                        )}
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: actionColor }}
                          >
                            {entry.action}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {dayjs(entry.timestamp).format("M/D/YYYY h:mm A")}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25 }}
                        >
                          {entry.description}
                        </Typography>

                        {/* Status change chip */}
                        {entry.changes?.status && (
                          <Box
                            sx={{
                              mt: 0.75,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.06,
                                ),
                              }}
                            >
                              {entryIsAvailable ? (
                                <CheckRoundedIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.success.main }}
                                />
                              ) : (
                                <ClearRoundedIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.error.main }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                {getStatusLabel(entryValue)}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Actor */}
                        {entry.userName && entry.userName !== "Unknown" && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, display: "block" }}
                          >
                            by {entry.userName}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}

      </DialogContent>
      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "12px",
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequirementHistoryDialog;
